/**
 * Générateur — Attribuer des probabilités dans des cas simples (équiprobabilité).
 * Attendu ID : 979 | Niveau : 5ème | Domaine : Probabilités (descripteur 132)
 *
 * Version conforme à la spécification 979 (2026-09-21).
 * Supprimées : les deux cartes précédentes, « Équiprobable ou pas ? » et « Le
 * dé suspect » (7 formats). Toutes deux reposent sur des dispositifs NON
 * symétriques — punaise, dé pipé, roue à secteurs inégaux, urne à boules de
 * tailles différentes, dé truqué dont on lit la fréquence — que `exclusions` 3
 * écarte sans réserve : « aucun dispositif non symétrique : une roue à secteurs
 * inégaux ou une urne dont l'énoncé ne donne pas les effectifs ne permet pas de
 * poser l'équiprobabilité, et l'objectif l'impose ». Décider si une situation
 * relève de l'équiprobabilité est l'objectif 977 ; ATTRIBUER une probabilité
 * sous un modèle déjà posé est celui-ci.
 * Ajoutées : une carte par niveau de la graduation, sur les cinq dispositifs
 * symétriques que la spec nomme.
 *
 * Plan de cartes :
 *   « Une chance sur combien ? » (niveau 1) — dispositif symétrique, évènement
 *     élémentaire, probabilité en fraction ; toute fraction de même valeur est
 *     acceptée (liste finie), la consigne n'exige pas l'irréductibilité.
 *   « Les cinq évènements » (niveau 2) — les cinq évènements que le programme
 *     nomme (impossible, certain, pile, une valeur d'un dé, une couleur de
 *     boule), chacun dans les trois écritures ; les dénominateurs divisent une
 *     puissance de dix, sauf le dé, dont la fraction seule est exacte.
 *   « Plusieurs cas favorables » (niveau 3) — évènement décrit par une
 *     PROPRIÉTÉ (un numéro pair, un numéro d'au plus k), jamais par « ou » ;
 *     l'élève dénombre les cas favorables puis forme le quotient, et relie
 *     l'énoncé à « x chances sur n ».
 *   « Une valeur approchée » (niveau 4) — la probabilité n'est pas décimale :
 *     la fraction exacte est donnée, l'élève en produit une valeur approchée au
 *     centième, introduite par le signe ≈, puis situe l'évènement sur l'échelle.
 *
 * Spécification appliquée :
 *   Bornes : 2 à 40 cas possibles ; dé à SIX faces ; dénominateur pris dans
 *     {2, 4, 5, 8, 10, 16, 20, 25, 32, 40} dès qu'une écriture décimale ou un
 *     pourcentage EXACTS sont demandés ; cas favorables de 0 au nombre de cas
 *     possibles, les deux extrêmes servis (probabilités 0 et 1) ; probabilité
 *     toujours dans [0 ; 1] ; arrondi au centième (probabilité) ou au dixième
 *     de pour cent au seul dernier niveau.
 *   Exclusions : aucun évènement contraire ; aucune réunion ni intersection,
 *     ni sous la forme « ou », ni sous la forme « et » (les évènements à
 *     plusieurs cas favorables sont décrits par une propriété) ; aucun
 *     dispositif non symétrique ; aucune expérience à deux épreuves ; aucune
 *     écriture décimale exacte demandée sur un dénominateur qui ne divise pas
 *     une puissance de dix ; aucune exigence d'irréductibilité qui ne soit
 *     écrite dans la consigne — et il n'y en a aucune.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - dénominateur pris sur les catégories visibles → `categories_pour_denominateur`
 *     - cas favorable compté deux fois → `cas_favorable_compte_deux_fois`
 *     - numérateur et dénominateur inversés (probabilité > 1) → `numerateur_denominateur_inverses`
 *     - refuser 0 ou 1 comme probabilité → `zero_ou_un_refuses`
 *     - « une chance sur quatre » traduit par 1,4 ou par 4 → `chance_sur_n_mal_traduite`
 *     - facteur 100 en trop ou en moins → `facteur_cent`
 *     - approximation présentée comme exacte → `approximation_presentee_comme_exacte`
 *   Graduation → cartes : 1 « Une chance sur combien ? » ; 2 « Les cinq
 *     évènements » ; 3 « Plusieurs cas favorables » ; 4 « Une valeur approchée ».
 *   Non tranché : la graduation range « une valeur donnée avec un dé
 *     équilibré » parmi les cinq évènements à écrire sous les TROIS formes, et
 *     demande en même temps que les dénominateurs rendent les trois écritures
 *     exactes ; or 6 ne divise aucune puissance de dix. Le dé n'est donc servi,
 *     au niveau 2, qu'en écriture fractionnaire, et son écriture décimale est
 *     renvoyée au niveau 4, où l'approximation est le sujet.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, fracTex, tex,
} from '../../../utils';

const ATTENDU_ID = 979;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CHANCE = 'Une chance sur combien ?';
const L_CINQ = 'Les cinq évènements';
const L_GROUPE = 'Plusieurs cas favorables';
const L_APPROCHE = 'Une valeur approchée';

/** Sous-ensemble employé au niveau 2 : la probabilité y compte au plus trois
 *  décimales, donc l'écriture décimale ET le pourcentage tiennent sur une ligne. */
const DENOM_CINQ = [10, 20, 25, 40];

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

/** Écritures admises d'une fraction : toutes les amplifications de la forme
 *  réduite jusqu'au quadruple du dénominateur donné, plus « 0 » et « 1 » pour
 *  les deux valeurs extrêmes. Liste FINIE et auditable (comparaison
 *  d'expression), aucune réponse juste n'est refusée en silence. */
const ecrituresFraction = (n, d) => {
    const g = pgcd(n === 0 ? d : n, d);
    const nr = n / g;
    const dr = d / g;
    const out = [];
    for (let k = 1; dr * k <= 4 * d; k++) out.push(`${nr * k}/${dr * k}`);
    if (n === 0) out.push('0');
    if (n === d) out.push('1');
    return [...new Set(out)];
};

/** Centièmes entiers → « 0,25 » / « 0,5 » / « 1 » / « 0 ». */
const centiFr = (c) => {
    if (c % 100 === 0) return String(c / 100);
    const r = String(c % 100).padStart(2, '0').replace(/0$/, '');
    return `${Math.floor(c / 100)},${r}`;
};

/** Millièmes entiers → « 0,025 » / « 0,5 » / « 1 ». */
const milliFr = (m) => {
    if (m % 1000 === 0) return String(m / 1000);
    const r = String(m % 1000).padStart(3, '0').replace(/0+$/, '');
    return `${Math.floor(m / 1000)},${r}`;
};

/** Dixièmes entiers → « 16,7 » / « 25 ». */
const dixiemeFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Garantit qu'un tableau vrai/faux n'est ni tout vrai ni tout faux. */
const equilibrer = (vs) => {
    const nbVrai = vs.filter(Boolean).length;
    if (nbVrai === 0) vs[0] = true;
    else if (nbVrai === vs.length) vs[0] = false;
    return vs;
};

// ─── Les cinq dispositifs SYMÉTRIQUES de la spécification ───

const dispoPiece = () => ({
    nature: 'pièce équilibrée',
    cas: 2,
    intro: '<p>On lance une pièce équilibrée et on note le côté obtenu. Les deux côtés sont '
        + 'interchangeables : par symétrie, ils ont la même probabilité.</p>',
    nomCas: (i) => (i === 0 ? 'PILE' : 'FACE'),
    evenement: (i) => `obtenir ${i === 0 ? 'PILE' : 'FACE'}`,
});

const dispoDe = () => ({
    nature: 'dé équilibré',
    cas: 6,
    faces: 6,
    intro: '<p>On lance un dé équilibré à six faces et on note le numéro obtenu. Les six faces sont '
        + 'interchangeables : par symétrie, elles ont la même probabilité.</p>',
    nomCas: (i) => String(i + 1),
    evenement: (i) => `obtenir le ${i + 1}`,
});

const dispoUrne = (n) => ({
    nature: 'tirage d\'une boule dans une urne',
    cas: n,
    intro: `<p>Une urne contient ${n} boules indiscernables au toucher, numérotées de 1 à ${n}. `
        + 'On en tire une au hasard. Les boules étant identiques, elles ont toutes la même probabilité '
        + 'd\'être tirées.</p>',
    nomCas: (i) => String(i + 1),
    evenement: (i) => `tirer la boule numéro ${i + 1}`,
});

const dispoRoue = (n) => ({
    nature: 'roue à secteurs égaux',
    cas: n,
    intro: `<p>Une roue est partagée en ${n} secteurs ÉGAUX, numérotés de 1 à ${n}. On la fait `
        + 'tourner et on note le secteur désigné. Les secteurs ayant la même taille, ils ont la même '
        + 'probabilité.</p>',
    nomCas: (i) => String(i + 1),
    evenement: (i) => `obtenir le secteur ${i + 1}`,
});

const dispoJeton = (n) => ({
    nature: 'tirage d\'une carte ou d\'un jeton numéroté',
    cas: n,
    intro: `<p>Un sac contient ${n} jetons identiques, numérotés de 1 à ${n}. On en tire un au `
        + 'hasard. Les jetons étant identiques, ils ont tous la même probabilité d\'être tirés.</p>',
    nomCas: (i) => String(i + 1),
    evenement: (i) => `tirer le jeton numéro ${i + 1}`,
});

/** Tire un dispositif symétrique dont le nombre de cas satisfait `filtre`. */
const tirerDispositif = (filtre) => {
    const candidats = [];
    if (filtre(2)) candidats.push(dispoPiece);
    if (filtre(6)) candidats.push(dispoDe);
    const numeros = [4, 5, 8, 10, 12, 15, 16, 20, 24, 25, 30, 32, 40];
    numeros.filter(filtre).forEach((n) => {
        candidats.push(() => dispoUrne(n));
        if (n <= 20) candidats.push(() => dispoRoue(n));
        candidats.push(() => dispoJeton(n));
    });
    if (!candidats.length) return dispoPiece();
    return randomChoice(candidats)();
};

const paramsProba = (d, niveau, favorables, ecritures, extra = {}) => ({
    niveau,
    nature_du_dispositif: d.nature,
    nombre_de_cas_possibles: d.cas,
    nombre_de_cas_favorables: favorables,
    valeur_de_la_probabilite: favorables / d.cas,
    ecriture_demandee_pour_la_probabilite: ecritures,
    ...(d.faces ? { nombre_de_faces_du_de: d.faces } : {}),
    ...extra,
});

// ═════════════════════════════════════════════════════════════
// CARTE « Une chance sur combien ? » (niveau 1 — fraction)
// ═════════════════════════════════════════════════════════════

const genChanceSaisie = () => {
    const d = tirerDispositif((n) => n >= 2 && n <= 40);
    const i = randInt(0, d.cas - 1);
    const ecritures = ecrituresFraction(1, d.cas);
    const payload = makeSaisie(`1/${d.cas}`,
        `${d.intro}<p>Quelle est la probabilité de l'évènement « ${d.evenement(i)} » ? `
        + 'Donne une fraction, tapée avec une barre, par exemple <code>3/8</code>. '
        + 'Il n\'est pas demandé de la simplifier.</p>',
        { ...opts(L_CHANCE), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = paramsProba(d, 1, 1, ['fraction'], { ecritures_admises: ecritures.length });
    return payload;
};

const genChanceQcm = () => {
    const d = tirerDispositif((n) => n >= 4 && n <= 40);
    const i = randInt(0, d.cas - 1);
    const bonne = fracTex(1, d.cas);
    const dist = [
        { code: 'numerateur_denominateur_inverses', valeur: fracTex(d.cas, 1) },
        { code: 'chance_sur_n_mal_traduite', valeur: tex(`1{,}${d.cas}`) },
        { code: 'categories_pour_denominateur', valeur: fracTex(1, 2) },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${d.intro}<p>On a une chance sur ${d.cas} d'obtenir l'évènement « ${d.evenement(i)} ». `
        + 'Quelle est sa probabilité ?</p>',
        opts(L_CHANCE));
    payload._meta.params = paramsProba(d, 1, 1, ['fraction'], { distracteurs: dist });
    return payload;
};

const genChanceVF = () => {
    const d = tirerDispositif((n) => n >= 4 && n <= 40);
    const i = randInt(0, d.cas - 1);
    const j = (i + 1) % d.cas;
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[1]) codes.push('numerateur_denominateur_inverses');
    if (!vs[2]) codes.push('chance_sur_n_mal_traduite');
    if (!vs[3]) codes.push('categories_pour_denominateur');
    const lignes = [
        [vs[0]
            ? `« ${d.evenement(i)} » et « ${d.evenement(j)} » ont la même probabilité, par symétrie du dispositif.`
            : `« ${d.evenement(i)} » est plus probable que « ${d.evenement(j)} », car il vient en premier.`, vs[0] ? 0 : 1],
        [`La probabilité de « ${d.evenement(i)} » vaut ${vs[1] ? fracTex(1, d.cas) : fracTex(d.cas, 1)}.`, vs[1] ? 0 : 1],
        [`« Une chance sur ${d.cas} » se note ${vs[2] ? fracTex(1, d.cas) : tex(`1{,}${d.cas}`)}.`, vs[2] ? 0 : 1],
        [`Le dénominateur de cette probabilité est ${vs[3] ? d.cas : 2}, le nombre de cas possibles.`, vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${d.intro}<p>Vrai ou faux ?</p>`, opts(L_CHANCE));
    payload._meta.params = paramsProba(d, 1, 1, ['fraction'], { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Les cinq évènements » (niveau 2 — les trois écritures)
// ═════════════════════════════════════════════════════════════

const COULEURS_BOULES = ['rouge', 'bleue', 'verte', 'jaune'];

/** Les cinq évènements que le programme nomme. Le dénominateur divise toujours
 *  une puissance de dix, sauf pour le dé, dont la spec fixe les six faces. */
const tirerCinq = () => {
    const quel = randInt(0, 4);
    if (quel === 2) {
        const d = dispoPiece();
        return { d, nom: 'obtenir PILE', fav: 1, exact: true, cle: 'pile' };
    }
    if (quel === 3) {
        const d = dispoDe();
        const i = randInt(0, 5);
        return { d, nom: `obtenir le ${i + 1}`, fav: 1, exact: false, cle: 'de' };
    }
    const n = randomChoice(DENOM_CINQ);
    if (quel === 4) {
        const couleurs = shuffle([...COULEURS_BOULES]).slice(0, 2);
        const c = randInt(2, n - 2);
        const d = {
            nature: 'tirage d\'une boule dans une urne',
            cas: n,
            intro: `<p>Une urne contient ${n} boules indiscernables au toucher : ${c} ${couleurs[0]}s `
                + `et ${n - c} ${couleurs[1]}s. On en tire une au hasard.</p>`,
            nomCas: (i) => String(i + 1),
            evenement: () => `tirer une boule ${couleurs[0]}`,
        };
        return { d, nom: `tirer une boule ${couleurs[0]}`, fav: c, exact: true, cle: 'couleur' };
    }
    const base = tirerDispositif((x) => x === n);
    if (quel === 0) {
        return { d: base, nom: `obtenir le numéro ${n + 1}`, fav: 0, exact: true, cle: 'impossible' };
    }
    return { d: base, nom: `obtenir un numéro compris entre 1 et ${n}`, fav: base.cas, exact: true, cle: 'certain' };
};

const introCinq = (e) => `${e.d.intro}<p>On considère l'évènement « ${e.nom} ».</p>`;

const paramsCinq = (e, ecritures, extra = {}) => paramsProba(e.d, 2, e.fav, ecritures, {
    nombre_d_evenements_a_ecrire_sous_les_trois_formes: 5,
    evenement_du_programme: e.cle,
    ...(e.exact ? { denominateur_compatible_avec_l_ecriture_decimale: e.d.cas } : {}),
    ...extra,
});

const genCinqSaisie = () => {
    const e = tirerCinq();
    const ecritures = ecrituresFraction(e.fav, e.d.cas);
    const payload = makeSaisie(`${e.fav}/${e.d.cas}`,
        `${introCinq(e)}<p>Donne sa probabilité sous forme de fraction, tapée avec une barre, `
        + 'par exemple <code>3/8</code>. Il n\'est pas demandé de la simplifier.</p>',
        { ...opts(L_CINQ), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = paramsCinq(e, ['fraction'], { ecritures_admises: ecritures.length });
    return payload;
};

const genCinqTrous = () => {
    let e = tirerCinq();
    for (let essai = 0; essai < 20 && !e.exact; essai++) e = tirerCinq();
    if (!e.exact) e = { d: dispoPiece(), nom: 'obtenir PILE', fav: 1, exact: true, cle: 'pile' };
    const milli = (e.fav * 1000) / e.d.cas;          // entier : cas divise 1000
    const phrase = `Fraction : ${e.fav} sur ${e.d.cas}\n`
        + `Écriture décimale : {{${milliFr(milli)}}}\n`
        + `Pourcentage : {{${dixiemeFr(milli)}}} %`;
    const payload = makeTexteTrou(phrase,
        `${introCinq(e)}<p>Sa probabilité s'écrit déjà en fraction : ${e.fav} sur ${e.d.cas}. `
        + 'Écris le MÊME nombre en écriture décimale, puis en pourcentage. Les deux écritures sont '
        + 'exactes ici : aucun arrondi n\'est demandé.</p>',
        { ...opts(L_CINQ), mode: 'saisie', strict: false });
    payload._meta.params = paramsCinq(e, ['décimale', 'pourcentage'], { nombre_de_trous: 2 });
    return payload;
};

const genCinqQcm = () => {
    const e = tirerCinq();
    if (e.fav === 0 || e.fav === e.d.cas) {
        const bonne = e.fav === 0 ? tex('0') : tex('1');
        const dist = [
            { code: 'zero_ou_un_refuses', valeur: 'Cet évènement n\'a pas de probabilité' },
            { code: 'zero_ou_un_refuses', valeur: e.fav === 0 ? 'Une probabilité ne peut pas valoir 0' : 'Une probabilité ne peut pas valoir 1' },
            { code: 'facteur_cent', valeur: e.fav === 0 ? tex('0{,}01') : tex('100') },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${introCinq(e)}<p>Quelle est sa probabilité ?</p>`,
            opts(L_CINQ));
        payload._meta.params = paramsCinq(e, ['fraction'], { distracteurs: dist });
        return payload;
    }
    if (!e.exact) {
        // Le dé : seule la fraction est exacte (voir « Non tranché » de l'en-tête).
        const bonne = fracTex(e.fav, e.d.cas);
        const dist = [
            { code: 'numerateur_denominateur_inverses', valeur: fracTex(e.d.cas, e.fav) },
            { code: 'categories_pour_denominateur', valeur: fracTex(e.fav, 2) },
            { code: 'facteur_cent', valeur: fracTex(100 * e.fav, e.d.cas) },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${introCinq(e)}<p>Quelle fraction donne sa probabilité ?</p>`,
            opts(L_CINQ));
        payload._meta.params = paramsCinq(e, ['fraction'], { distracteurs: dist });
        return payload;
    }
    const milli = (e.fav * 1000) / e.d.cas;
    const bonne = `${dixiemeFr(milli)} %`;
    // Les quatre propositions sont distinctes par construction : la bonne vaut
    // milli/10 (au plus 100), le facteur cent oublié vaut milli/1000 (au plus 1),
    // le facteur dix vaut milli (au moins 10) et l'inversion vaut 100 × cas/fav
    // (au moins 100, et égale à 100 seulement si cas = fav, cas écarté ici).
    const dist = [
        { code: 'facteur_cent', valeur: `${milliFr(milli)} %` },
        { code: 'facteur_cent', valeur: `${dixiemeFr(milli * 10)} %` },
        { code: 'numerateur_denominateur_inverses', valeur: `${dixiemeFr(Math.round((e.d.cas * 1000) / e.fav))} %` },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introCinq(e)}<p>Sa probabilité vaut ${e.fav} sur ${e.d.cas}. Quel pourcentage donne le même nombre ?</p>`,
        opts(L_CINQ));
    payload._meta.params = paramsCinq(e, ['pourcentage'], { distracteurs: dist });
    return payload;
};

const genCinqVF = () => {
    const e = tirerCinq();
    const milli = e.exact ? (e.fav * 1000) / e.d.cas : 0;   // entier quand l'écriture est exacte
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('zero_ou_un_refuses');
    if (!vs[1]) codes.push('zero_ou_un_refuses');
    if (!vs[2]) codes.push(e.exact ? 'facteur_cent' : 'approximation_presentee_comme_exacte');
    if (!vs[3]) codes.push('approximation_presentee_comme_exacte');
    const lignes = [
        [vs[0]
            ? 'Une probabilité est un nombre compris entre 0 et 1, bornes comprises.'
            : 'Une probabilité est un nombre strictement compris entre 0 et 1.', vs[0] ? 0 : 1],
        [vs[1]
            ? 'Un évènement impossible a une probabilité de 0, et un évènement certain une probabilité de 1.'
            : 'Un évènement impossible et un évènement certain n\'ont pas de probabilité.', vs[1] ? 0 : 1],
        [e.exact
            ? `La probabilité « ${e.fav} sur ${e.d.cas} » s'écrit ${vs[2] ? dixiemeFr(milli) : dixiemeFr(milli * 10)} % en pourcentage.`
            : (vs[2]
                ? `La probabilité « ${e.fav} sur ${e.d.cas} » n'a pas d'écriture décimale exacte.`
                : `La probabilité « ${e.fav} sur ${e.d.cas} » s'écrit exactement 0,17.`), vs[2] ? 0 : 1],
        [vs[3]
            ? 'Une probabilité s\'écrit en fraction, en écriture décimale et en pourcentage : c\'est le même nombre.'
            : 'Une probabilité écrite en fraction et la même écrite en pourcentage sont deux nombres différents.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${introCinq(e)}<p>Vrai ou faux ?</p>`, opts(L_CINQ));
    payload._meta.params = paramsCinq(e, e.exact ? ['fraction', 'décimale', 'pourcentage'] : ['fraction'], {
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Plusieurs cas favorables » (niveau 3)
// ═════════════════════════════════════════════════════════════

/** Évènement décrit par une PROPRIÉTÉ, jamais par « ou » : la spec exclut la
 *  réunion sous toutes ses formes. */
const tirerGroupe = () => {
    const d = tirerDispositif((n) => n >= 6 && n <= 40);
    const type = randInt(0, 2);
    if (type === 0) {
        return { d, nom: 'obtenir un numéro pair', fav: Math.floor(d.cas / 2), propriete: 'pair' };
    }
    if (type === 1) {
        const k = randInt(2, d.cas - 2);
        return { d, nom: `obtenir un numéro inférieur ou égal à ${k}`, fav: k, propriete: 'seuil' };
    }
    const m = d.cas >= 12 ? 3 : 2;
    return { d, nom: `obtenir un multiple de ${m}`, fav: Math.floor(d.cas / m), propriete: 'multiple' };
};

/** Le dé et la pièce nomment des faces, pas des numéros : on reformule. */
const nomGroupe = (g) => (g.d.nature === 'dé équilibré' ? g.nom.replace('numéro', 'nombre') : g.nom);

const genGroupeSaisie = () => {
    const g = tirerGroupe();
    const ecritures = ecrituresFraction(g.fav, g.d.cas);
    const payload = makeSaisie(`${g.fav}/${g.d.cas}`,
        `${g.d.intro}<p>On considère l'évènement « ${nomGroupe(g)} ». Compte d'abord les cas `
        + 'favorables, puis donne la probabilité sous forme de fraction (par exemple <code>3/8</code>). '
        + 'Il n\'est pas demandé de la simplifier.</p>',
        { ...opts(L_GROUPE), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = paramsProba(g.d, 3, g.fav, ['fraction'], {
        propriete_de_l_evenement: g.propriete,
        erreurVisee: 'cas_favorable_compte_deux_fois',
        ecritures_admises: ecritures.length,
    });
    return payload;
};

const genGroupeQcm = () => {
    const g = tirerGroupe();
    const bonne = `${g.fav} chances sur ${g.d.cas}`;
    const dist = [
        { code: 'categories_pour_denominateur', valeur: `${g.fav} chances sur ${g.fav + 1}` },
        { code: 'numerateur_denominateur_inverses', valeur: `${g.d.cas} chances sur ${g.fav}` },
        { code: 'cas_favorable_compte_deux_fois', valeur: `${2 * g.fav} chances sur ${g.d.cas}` },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${g.d.intro}<p>On considère l'évènement « ${nomGroupe(g)} ». Comment dire sa probabilité `
        + 'avec l\'expression « x chances sur n » ?</p>',
        opts(L_GROUPE));
    payload._meta.params = paramsProba(g.d, 3, g.fav, ['fraction'], {
        propriete_de_l_evenement: g.propriete,
        distracteurs: dist,
    });
    return payload;
};

const genGroupeVF = () => {
    const g = tirerGroupe();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('cas_favorable_compte_deux_fois');
    if (!vs[1]) codes.push('categories_pour_denominateur');
    if (!vs[2]) codes.push('numerateur_denominateur_inverses');
    const lignes = [
        [`L'évènement « ${nomGroupe(g)} » compte ${vs[0] ? g.fav : 2 * g.fav} cas favorables.`, vs[0] ? 0 : 1],
        [`Le dénominateur de cette probabilité est ${vs[1] ? g.d.cas : g.fav}, le nombre de cas possibles.`, vs[1] ? 0 : 1],
        [`Cette probabilité vaut ${vs[2] ? fracTex(g.fav, g.d.cas) : fracTex(g.d.cas, g.fav)}.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Une probabilité ne peut jamais dépasser 1.'
            : 'Une probabilité peut dépasser 1 quand il y a beaucoup de cas favorables.', vs[3] ? 0 : 1],
    ];
    if (!vs[3]) codes.push('numerateur_denominateur_inverses');
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${g.d.intro}<p>On considère l'évènement « ${nomGroupe(g)} ». Vrai ou faux ?</p>`,
        opts(L_GROUPE));
    payload._meta.params = paramsProba(g.d, 3, g.fav, ['fraction'], {
        propriete_de_l_evenement: g.propriete,
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Une valeur approchée » (niveau 4)
// ═════════════════════════════════════════════════════════════

/** Probabilité NON décimale : le dénominateur porte un facteur 3 ou 7. */
const tirerApproche = () => {
    const n = randomChoice([6, 12, 15, 24, 30]);      // chacun porte un facteur 3
    const d = tirerDispositif((x) => x === n);
    return {
        d,
        fav: 1,
        indice: randInt(0, n - 1),          // quel cas élémentaire : fait varier l'énoncé
        cas: n,
        centi: Math.round(100 / n),
        milli: Math.round(1000 / n),
    };
};

const introApproche = (a) => `${a.d.intro}<p>On considère l'évènement « ${a.d.evenement(a.indice)} ». `
    + `Sa probabilité vaut exactement ${a.fav} sur ${a.cas}, un nombre qui n'a PAS d'écriture `
    + 'décimale exacte.</p>';

const genApprocheTrous = () => {
    const a = tirerApproche();
    const phrase = `Valeur approchée au centième : {{${centiFr(a.centi)}}}\n`
        + `Pourcentage arrondi au dixième : {{${dixiemeFr(a.milli)}}} %`;
    const payload = makeTexteTrou(phrase,
        `${introApproche(a)}<p>Donne une valeur approchée de cette probabilité au CENTIÈME, puis le `
        + 'pourcentage correspondant arrondi au DIXIÈME. Ces deux écritures sont approchées : on les '
        + 'introduit par le signe ≈, jamais par un signe égal.</p>',
        { ...opts(L_APPROCHE), mode: 'saisie', strict: false });
    payload._meta.params = paramsProba(a.d, 4, a.fav, ['décimale', 'pourcentage'], {
        arrondi_de_la_probabilite_decimale: 2,
        quotient_exact: false,
        erreurVisee: 'approximation_presentee_comme_exacte',
        nombre_de_trous: 2,
    });
    return payload;
};

const genApprocheQcm = () => {
    const a = tirerApproche();
    const bonne = `${a.fav} sur ${a.cas} ≈ ${centiFr(a.centi)}`;
    const dist = [
        { code: 'approximation_presentee_comme_exacte', valeur: `${a.fav} sur ${a.cas} = ${centiFr(a.centi)}` },
        { code: 'approximation_presentee_comme_exacte', valeur: `${a.fav} sur ${a.cas} = ${centiFr(a.centi)} exactement` },
        { code: 'facteur_cent', valeur: `${a.fav} sur ${a.cas} ≈ ${a.centi}` },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introApproche(a)}<p>On en donne une valeur approchée au centième. Quelle écriture est correcte ?</p>`,
        opts(L_APPROCHE));
    payload._meta.params = paramsProba(a.d, 4, a.fav, ['décimale'], {
        arrondi_de_la_probabilite_decimale: 2,
        distracteurs: dist,
    });
    return payload;
};

const genApprocheVF = () => {
    const a = tirerApproche();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('approximation_presentee_comme_exacte');
    if (!vs[1]) codes.push('approximation_presentee_comme_exacte');
    if (!vs[2]) codes.push('facteur_cent');
    if (!vs[3]) codes.push('zero_ou_un_refuses');
    const proche = a.centi < 50 ? '0' : '1';
    const loin = a.centi < 50 ? '1' : '0';
    const lignes = [
        [`${a.fav} sur ${a.cas} ${vs[0] ? 'vaut environ' : 'vaut exactement'} ${centiFr(a.centi)}.`, vs[0] ? 0 : 1],
        [vs[1]
            ? 'Une valeur approchée s\'introduit par le signe ≈, et la fraction reste la seule écriture exacte.'
            : 'Une valeur approchée au centième peut s\'écrire avec un signe égal.', vs[1] ? 0 : 1],
        [`En pourcentage, cette probabilité vaut environ ${vs[2] ? dixiemeFr(a.milli) : dixiemeFr(a.milli * 10)} %.`, vs[2] ? 0 : 1],
        [`Sur l'échelle de 0 à 1, cette probabilité se place près de ${vs[3] ? proche : loin}.`, vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${introApproche(a)}<p>On en cherche une valeur approchée au centième. Vrai ou faux ?</p>`,
        opts(L_APPROCHE));
    payload._meta.params = paramsProba(a.d, 4, a.fav, ['décimale', 'pourcentage'], {
        arrondi_de_la_probabilite_decimale: 2,
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    chance_texte: genChanceSaisie,
    chance_qcm_unique: genChanceQcm,
    chance_tableau_vf: genChanceVF,
    cinq_texte: genCinqSaisie,
    cinq_texte_trou_saisie: genCinqTrous,
    cinq_qcm_unique: genCinqQcm,
    cinq_tableau_vf: genCinqVF,
    groupe_texte: genGroupeSaisie,
    groupe_qcm_unique: genGroupeQcm,
    groupe_tableau_vf: genGroupeVF,
    approche_texte_trou_saisie: genApprocheTrous,
    approche_qcm_unique: genApprocheQcm,
    approche_tableau_vf: genApprocheVF,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'chance', label: L_CHANCE, formatsList: buildFormats('chance') },
    { id: 'cinq', label: L_CINQ, formatsList: buildFormats('cinq') },
    { id: 'groupe', label: L_GROUPE, formatsList: buildFormats('groupe') },
    { id: 'approche', label: L_APPROCHE, formatsList: buildFormats('approche') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
